import React, { useState, useRef, useEffect } from 'react';
import Button from 'components/ui/Button';
import Icon from 'components/AppIcon';
import serviceRequestService from 'services/serviceRequestService';
import { supabase } from 'lib/supabase';
import { useNavigate } from 'react-router-dom';

// Simple toast helper
const showToast = (message, type = 'success') => {
  const toast = document.createElement('div');
  toast.textContent = message;
  toast.style.cssText = `
    position: fixed; bottom: 24px; right: 24px; z-index: 9999;
    padding: 12px 20px; border-radius: 8px; font-size: 14px; font-weight: 500;
    color: white; box-shadow: 0 4px 12px rgba(0,0,0,0.15);
    background: ${type === 'error' ? '#ef4444' : '#22c55e'};
    animation: fadeIn 0.2s ease;
  `;
  document.body?.appendChild(toast);
  setTimeout(() => toast?.remove(), 3500);
};

const QuoteModal = ({ request, currentUserId, onClose, onSuccess }) => {
  const [previewPrice, setPreviewPrice] = useState('');
  const [previewText, setPreviewText] = useState('');
  const [pdfFile, setPdfFile] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const fileInputRef = useRef(null);

  const handleFileChange = (e) => {
    const file = e?.target?.files?.[0];
    if (!file) return;
    if (file?.size > 10 * 1024 * 1024) {
      showToast('Il file PDF supera i 10MB consentiti', 'error');
      e.target.value = '';
      return;
    }
    setPdfFile(file);
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    if (!previewPrice || !previewText?.trim()) return;

    setSubmitting(true);
    try {
      // 1. Check for duplicate quote
      const { data: existing, error: dupError } = await supabase
        ?.from('quotes')
        ?.select('id')
        ?.eq('service_request_id', request?.id)
        ?.eq('professional_id', currentUserId)
        ?.maybeSingle();

      if (dupError) throw dupError;
      if (existing) {
        showToast('Hai già inviato un preventivo', 'error');
        setSubmitting(false);
        return;
      }

      // 2. Upload PDF if attached
      let pdfUrl = null;
      if (pdfFile) {
        const filePath = `${currentUserId}/${request?.id}/preventivo.pdf`;
        const { data: uploadData, error: uploadError } = await supabase
          ?.storage
          ?.from('quote-pdfs')
          ?.upload(filePath, pdfFile, { upsert: true, contentType: 'application/pdf' });

        if (uploadError) throw uploadError;

        const { data: urlData } = supabase
          ?.storage
          ?.from('quote-pdfs')
          ?.getPublicUrl(uploadData?.path);
        pdfUrl = urlData?.publicUrl || null;
      }

      // 3. Insert into quotes
      const { error: insertError } = await supabase
        ?.from('quotes')
        ?.insert({
          service_request_id: request?.id,
          professional_id: currentUserId,
          preview_price: parseFloat(previewPrice),
          preview_text: previewText?.trim(),
          status: 'sent',
          pdf_url: pdfUrl
        });

      if (insertError) throw insertError;

      // 4. Update request_matches status to 'quoted'
      await supabase
        ?.from('request_matches')
        ?.update({ status: 'quoted' })
        ?.eq('request_id', request?.id)
        ?.eq('professional_id', currentUserId);

      // 5. Success
      showToast('Preventivo inviato');
      onSuccess?.();
      onClose();
    } catch (err) {
      console.error('Error submitting quote:', err);
      showToast(err?.message || 'Errore durante l\'invio del preventivo', 'error');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
      onClick={(e) => { if (e?.target === e?.currentTarget) onClose(); }}
    >
      <div className="bg-white rounded-xl shadow-xl w-full max-w-lg">
        {/* Modal Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">Invia preventivo</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
            aria-label="Chiudi"
          >
            <Icon name="X" size={20} />
          </button>
        </div>

        {/* Modal Body */}
        <form onSubmit={handleSubmit} className="px-6 py-5 space-y-5">
          {/* Preview Price */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Prezzo stimato (€) <span className="text-red-500">*</span>
            </label>
            <input
              type="number"
              min="0"
              step="0.01"
              required
              value={previewPrice}
              onChange={(e) => setPreviewPrice(e?.target?.value)}
              placeholder="es. 250.00"
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
            />
          </div>

          {/* Preview Text */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Descrivi il tuo approccio <span className="text-red-500">*</span>
            </label>
            <textarea
              required
              maxLength={500}
              rows={5}
              value={previewText}
              onChange={(e) => setPreviewText(e?.target?.value)}
              placeholder="Descrivi come intendi affrontare questa richiesta..."
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent resize-none"
            />
            <p className="text-xs text-gray-400 text-right mt-1">{previewText?.length}/500</p>
          </div>

          {/* PDF Upload */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Allega preventivo PDF <span className="text-gray-400 font-normal">(opzionale, max 10MB)</span>
            </label>
            <div
              className="border border-dashed border-gray-300 rounded-lg px-4 py-3 flex items-center gap-3 cursor-pointer hover:border-primary transition-colors"
              onClick={() => fileInputRef?.current?.click()}
            >
              <Icon name="Paperclip" size={16} className="text-gray-400 flex-shrink-0" />
              <span className="text-sm text-gray-500 truncate">
                {pdfFile ? pdfFile?.name : 'Clicca per allegare un PDF'}
              </span>
              {pdfFile && (
                <button
                  type="button"
                  onClick={(e) => { e?.stopPropagation(); setPdfFile(null); if (fileInputRef?.current) fileInputRef.current.value = ''; }}
                  className="ml-auto text-gray-400 hover:text-red-500 transition-colors"
                >
                  <Icon name="X" size={14} />
                </button>
              )}
            </div>
            <input
              ref={fileInputRef}
              type="file"
              accept=".pdf"
              className="hidden"
              onChange={handleFileChange}
            />
          </div>

          {/* Footer Actions */}
          <div className="flex items-center gap-3 pt-1">
            <Button
              type="button"
              variant="outline"
              size="default"
              onClick={onClose}
              className="flex-1"
              disabled={submitting}
            >
              Annulla
            </Button>
            <Button
              type="submit"
              variant="default"
              size="default"
              loading={submitting}
              disabled={submitting}
              iconName="Send"
              className="flex-1"
            >
              Invia preventivo
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

const ServiceRequestCard = ({ request, currentUserId, matchStatus, onViewed }) => {
  const [expanded, setExpanded] = useState(false);
  const [accepting, setAccepting] = useState(false);
  const [acceptError, setAcceptError] = useState(null);
  const [currentMatchStatus, setCurrentMatchStatus] = useState(matchStatus);
  const [showQuoteModal, setShowQuoteModal] = useState(false);
  const [hasQuoted, setHasQuoted] = useState(null);
  const navigate = useNavigate();

  const isExclusive = request?.assigned_professional_id && currentUserId && request?.assigned_professional_id === currentUserId;

  useEffect(() => {
    if (!currentUserId || !request?.id) return;
    let cancelled = false;
    const checkQuote = async () => {
      const { data } = await supabase
        ?.from('quotes')
        ?.select('id')
        ?.eq('service_request_id', request?.id)
        ?.eq('professional_id', currentUserId)
        ?.maybeSingle();
      if (!cancelled) {
        setHasQuoted(data !== null);
      }
    };
    checkQuote();
    return () => { cancelled = true; };
  }, [currentUserId, request?.id]);

  const urgencyColors = {
    low: 'bg-blue-100 text-blue-800',
    medium: 'bg-yellow-100 text-yellow-800',
    high: 'bg-orange-100 text-orange-800',
    urgent: 'bg-red-100 text-red-800'
  };

  const urgencyLabels = {
    low: 'Bassa',
    medium: 'Media',
    high: 'Alta',
    urgent: 'Urgente'
  };

  const handleAccept = async () => {
    setAccepting(true);
    setAcceptError(null);

    const { success, error } = await serviceRequestService?.acceptRequest(request?.id);

    if (success) {
      // Request will be removed from list via real-time subscription
    } else {
      setAcceptError(error);
    }

    setAccepting(false);
  };

  const handleToggleExpand = async () => {
    const newExpanded = !expanded;
    setExpanded(newExpanded);

    // Mark as viewed if opening and status is still 'pending'
    if (newExpanded && currentMatchStatus === 'pending' && currentUserId && request?.id) {
      try {
        const { error } = await supabase
          ?.from('request_matches')
          ?.update({ status: 'viewed', viewed_at: new Date()?.toISOString() })
          ?.eq('request_id', request?.id)
          ?.eq('professional_id', currentUserId)
          ?.eq('status', 'pending');

        if (!error) {
          setCurrentMatchStatus('viewed');
          onViewed?.();
        }
      } catch (err) {
        console.error('Error marking request as viewed:', err);
      }
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Non specificata';
    const date = new Date(dateString);
    return date?.toLocaleDateString('it-IT', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatCompensation = (amount) => {
    if (!amount) return 'Da concordare';
    return `€${amount?.toFixed(2)}`;
  };

  return (
    <>
      <div className="border border-gray-200 rounded-lg p-6 hover:shadow-md transition-shadow bg-white">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 sm:gap-4 mb-4">
          <div className="flex-1">
            <div className="flex items-center gap-3 mb-2">
              <h3 className="text-lg font-semibold text-gray-900">{request?.title}</h3>
              {isExclusive && (
                <span
                  style={{
                    backgroundColor: '#013776',
                    color: '#ffffff',
                    padding: '4px 10px',
                    borderRadius: '999px',
                    fontSize: '11px',
                    fontWeight: '700',
                    letterSpacing: '0.05em'
                  }}
                >
                  RICHIESTA ESCLUSIVA
                </span>
              )}
              <span
                className={`px-2 py-1 rounded-full text-xs font-medium ${
                  urgencyColors?.[request?.urgency] || urgencyColors?.medium
                }`}
              >
                {urgencyLabels?.[request?.urgency] || 'Media'}
              </span>
              {hasQuoted === true && (
                <span className="px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-700">
                  Preventivo inviato
                </span>
              )}
              {hasQuoted === false && currentMatchStatus === 'pending' && (
                <span className="px-2 py-1 rounded-full text-xs font-medium bg-indigo-100 text-indigo-700">
                  Nuova
                </span>
              )}
              {hasQuoted === false && currentMatchStatus === 'viewed' && (
                <span className="px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-600">
                  Vista
                </span>
              )}
            </div>
            <div className="flex items-center gap-4 text-sm text-gray-600">
              <span className="flex items-center gap-1">
                <Icon name="Briefcase" size={14} />
                {request?.service_type}
              </span>
              <span className="flex items-center gap-1">
                <Icon name="MapPin" size={14} />
                {request?.location_name}
              </span>
            </div>
          </div>
          <div className="text-left sm:text-right flex-shrink-0">
            <p className="text-xl sm:text-2xl font-bold text-primary leading-tight">
              {formatCompensation(request?.estimated_compensation)}
            </p>
            <p className="text-xs text-gray-500">Compenso stimato</p>
          </div>
        </div>

        {/* Description Preview */}
        <p className="text-gray-700 mb-4">
          {expanded
            ? request?.description
            : `${request?.description?.substring(0, 150)}${request?.description?.length > 150 ? '...' : ''}`}
        </p>
        {isExclusive && (
          <p
            style={{
              fontSize: '13px',
              color: '#2780b5',
              fontStyle: 'italic',
              marginTop: '12px',
              marginBottom: '16px',
              padding: '10px 12px',
              backgroundColor: '#eff6ff',
              borderRadius: '8px',
              borderLeft: '3px solid #2780b5',
              lineHeight: '1.5'
            }}
          >
            Questa richiesta è arrivata dalla tua pagina pubblica. Solo tu puoi rispondere.
          </p>
        )}

        {/* Already Quoted Banner */}
        {hasQuoted === true && (
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-4 flex items-center justify-between gap-3">
            <p className="text-sm text-amber-800">Hai già inviato un preventivo per questa richiesta.</p>
            <button
              onClick={() => navigate(`/send-quote/${request?.id}`)}
              className="text-sm font-medium text-amber-800 underline whitespace-nowrap hover:text-amber-900 transition-colors"
            >
              Modificalo
            </button>
          </div>
        )}

        {/* Expandable Details */}
        {expanded && (
          <div className="bg-gray-50 rounded-lg p-4 mb-4 space-y-3">
            <div className="flex items-start gap-2">
              <Icon name="Clock" size={16} className="text-gray-500 mt-1" />
              <div>
                <p className="text-sm font-medium text-gray-700">Scadenza risposta</p>
                <p className="text-sm text-gray-600">{formatDate(request?.response_deadline)}</p>
              </div>
            </div>
            <div className="flex items-start gap-2">
              <Icon name="MapPin" size={16} className="text-gray-500 mt-1" />
              <div>
                <p className="text-sm font-medium text-gray-700">Località completa</p>
                <p className="text-sm text-gray-600">
                  {request?.location_full_name || request?.location_name}
                </p>
              </div>
            </div>
            <div className="flex items-start gap-2">
              <Icon name="Calendar" size={16} className="text-gray-500 mt-1" />
              <div>
                <p className="text-sm font-medium text-gray-700">Pubblicata il</p>
                <p className="text-sm text-gray-600">{formatDate(request?.created_at)}</p>
              </div>
            </div>
          </div>
        )}

        {/* Error Message */}
        {acceptError && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-4 flex items-start gap-2">
            <Icon name="AlertCircle" size={16} className="text-red-600 flex-shrink-0 mt-0.5" />
            <p className="text-sm text-red-800">{acceptError}</p>
          </div>
        )}

        {/* Actions */}
        <div className="flex items-center gap-3">
          <Button
            variant="default"
            size="default"
            onClick={() => navigate(`/request-detail/${request?.id}`)}
            iconName="Eye"
            className="flex-1"
          >
            Vedi richiesta
          </Button>
          <Button
            variant="outline"
            size="default"
            onClick={handleToggleExpand}
            iconName={expanded ? 'ChevronUp' : 'ChevronDown'}
          >
            {expanded ? 'Riduci' : 'Dettagli'}
          </Button>
        </div>
      </div>

      {/* Quote Modal */}
      {showQuoteModal && (
        <QuoteModal
          request={request}
          currentUserId={currentUserId}
          onClose={() => setShowQuoteModal(false)}
          onSuccess={() => setCurrentMatchStatus('quoted')}
        />
      )}
    </>
  );
};

export default ServiceRequestCard;
